/**
 * ONE CLIENT, MADE ONCE.
 *
 * Every screen shares this. The system it replaces created a Supabase
 * client wherever one was needed, which meant several live sockets, several
 * auth listeners, and a sign-out that only some of them noticed.
 *
 * The URL and anon key are not compiled in. They come from the server at
 * boot, so the same bundle runs against staging and production without a
 * rebuild — and so a key can be rotated by changing an environment variable
 * rather than by shipping a new file.
 */

import { customerOrigin } from "../domain/smsbrand.ts";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

export type AppConfig = {
  supabaseUrl: string;
  supabaseAnonKey: string;
  stage: string;
  /**
   * What this deployment can actually do. Staging has no SMS and no card
   * gateway, so the screens hide those buttons rather than offering an
   * action that quietly cannot happen.
   */
  /**
   * `push` is not a button this hides. It is whether the app may ask for
   * the notification permission at all -- see canSendPush() in server/env.ts
   * and the note in src/data/push.ts. False until something can deliver.
   */
  features: { sms: boolean; payments: boolean; labour: boolean; push: boolean };
  /** Where a finished customer is asked to leave a review. */
  reviewUrl: string;
  /** Per-market overrides, keyed by market: { hemet: "https://…" }. */
  reviewLinks: Record<string, string>;
  /**
   * WHOSE FRONT DOOR THIS IS, when the host is a shop's own domain.
   *
   * Null on Supercanic's hosts. It exists for the sign-in screen, which is
   * the one page nobody has a session on -- row level security means the
   * browser cannot read an account until it is signed in, so the server
   * has to say who this domain belongs to.
   */
  /** "rvdesk" on rvdesk.com hosts, else null or absent. src/domain/platform.ts. */
  platform?: "rvdesk" | null;
  shop: { name: string; phone: string | null; domain: string;
    /** Their own logo, and their identity colour. Null falls back to
     *  initials on a square and the house green. Migration 0116. */
    logoUrl: string | null;
    /** The same logo for a LIGHT background. Falls back to logoUrl.
     *  Lettering on transparency vanishes against the wrong surface. */
    logoUrlLight: string | null;
    brandColour: string | null } | null;
};

/**
 * WHERE THE API LIVES.
 *
 * Empty in a browser, because the page and the API are the same origin and
 * a relative path is the right answer. NOT empty inside the app store
 * build: a Capacitor shell serves its own files from capacitor://localhost,
 * so "/api/config" would resolve against the phone and reach nothing.
 *
 * Baked at build time rather than sniffed at runtime. A wrapper that
 * guesses its own back end gets it wrong exactly once, in the field, on
 * somebody's phone, with no console to look at.
 */
declare const __API_BASE__: string | undefined;
export const API_BASE =
  typeof __API_BASE__ === "string" ? __API_BASE__.replace(/\/$/, "") : "";

/** Every call to our own server goes through this. */
export const api = (path: string): string => `${API_BASE}${path}`;

/**
 * WHERE THE PUBLIC SITE IS, for links a CUSTOMER will open.
 *
 * On the web this is the page's own origin and nothing changes. Inside the
 * app store shell it is NOT: location.origin there is `capacitor://localhost`
 * on iOS and `http://localhost` on Android. A technician texting a customer
 * their vehicle page from the app would have sent them
 * `capacitor://localhost/garage.html?t=...` -- a link that opens nothing on
 * anybody's phone, and no error anywhere to say so.
 *
 * API_BASE is baked in at build time by `npm run build:app`, so in the shell
 * it is the real site and on the web it is empty, which leaves the origin
 * exactly as it was.
 */
export const publicSite = (): string =>
  API_BASE || location.origin.replace(/\/$/, "");

/**
 * WHERE A LINK FOR THIS SHOP'S CUSTOMER POINTS.
 *
 * publicSite() is wherever the person pressing the button signed in, which
 * for most shops is shop.supercanic.com. A Wrench Services customer handed
 * a supercanic.com link reads another company's name in their own shop's
 * text, and an A2P reviewer reads it too. A shop with accounts.domain set
 * (shop.wrenchservices.com) gets links on its own host; the server builds
 * the automated texts' links the same way (server/desk/customer-site.ts).
 * Primed at boot from the account whoAmI() loaded. Unset, it is
 * publicSite(), exactly as before.
 */
let shopDomain: string | null = null;
export function primeShopDomain(domain: string | null | undefined): void {
  shopDomain = String(domain ?? "").trim() || null;
}
export const customerSite = (): string => customerOrigin(shopDomain, publicSite());

let config: AppConfig | null = null;
let client: SupabaseClient | null = null;

/** Fetched once. Everything after boot reads the cached answer. */
export async function loadConfig(): Promise<AppConfig> {
  if (config) return config;
  const res = await fetch(api("/api/config"));
  if (!res.ok) throw new Error(`the server would not describe itself (${res.status})`);
  config = (await res.json()) as AppConfig;
  return config;
}

export async function db(): Promise<SupabaseClient> {
  if (client) return client;
  const c = await loadConfig();
  client = createClient(c.supabaseUrl, c.supabaseAnonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
  return client;
}

/** Read the cached config synchronously, once boot has done its job. */
export function configNow(): AppConfig {
  if (!config) throw new Error("config read before boot finished");
  return config;
}
